import { putBannerOrderNumberAPI } from '@/src/apis/rest/banner';
import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import DndItem from '@/src/components/atom/DndItem';
import LocalIcon from '@/src/components/atom/Icons/LocalIcon';
import ImagePreview from '@/src/components/molecule/ImagePreview';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import useDnd from '@/src/hooks/useDnd';
import { useToast } from '@/src/hooks/useToast';
import { BannerDto } from '@/src/models/BannerDto';
import { useMutation } from '@tanstack/react-query';
import { GripVertical } from 'lucide-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TablePlaceholderSvg from '@/assets/icons/icon-table-placeholder.svg';
import Typo from '@/src/components/atom/Typo';

interface OrderModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOkClick: () => void;
  items: BannerDto[];
}

export default function OrderModal({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOkClick,
  items: itemsProps,
}: OrderModalProps) {
  const { toast } = useToast();
  const { moveInsertCardHandler, items } = useDnd(itemsProps);

  // mutation
  const putBannerOrderNumberMutation = useMutation({
    mutationFn: putBannerOrderNumberAPI,
  });

  // event
  const handleCloseModal = () => {
    onOpenChangeProps(false);
  };
  const handleOkClick = () => {
    putBannerOrderNumberMutation.mutate(
      {
        data: items.map((item, idx) => ({
          bannerOrderNumber: idx + 1,
          id: item.id,
        })),
      },
      {
        onSuccess: () => {
          toast({
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
            variant: 'success',
          });
          onOkClick();
          handleCloseModal();
        },
        onError: e => {
          toast({
            title: TOAST_CONST.error.title,
            description: e.message,
            variant: 'error',
          });
        },
      },
    );
  };

  // render
  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          className="flex-1"
          onClick={handleOkClick}
          isLoading={putBannerOrderNumberMutation.isPending}
          disabled={!items?.length}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  return (
    <Dialog
      open={openProps}
      onOpenChange={onOpenChangeProps}
      contentProps={{
        style: {
          width: 'calc(100vw - 24px)',
        },
        className: 'max-w-[967px]',
      }}
      title="메인 배너 순번관리"
      footer={renderFooter()}
    >
      <DndProvider backend={HTML5Backend}>
        <div className="flex flex-col gap-4">
          <div className="flex gap-5 text-b14sb text-gray6">
            <p className="w-[80px] flex-shrink-0 flex-grow-0 pr-1 text-right">
              No.
            </p>
            <p className="flex-1">메인 배너 노출 목록</p>
          </div>
          <div className="flex flex-col gap-3">
            {items?.length ? (
              items.map((item, index) => (
                <DndItem
                  key={item.id}
                  index={index}
                  moveCardHandler={moveInsertCardHandler}
                  className="flex gap-2"
                >
                  <div className="flex flex-1 cursor-move items-center gap-5 rounded-xl border border-gray2">
                    <div className="flex flex-1 gap-5 p-3">
                      <GripVertical width={24} height={24} />
                      <p className="flex min-w-6 flex-shrink-0 items-center justify-center text-h16b">
                        {index + 1}
                      </p>
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <p className="text-b16r">{item.bannerTitle}</p>
                      </div>
                    </div>
                    <ImagePreview
                      imgSrc={item?.bannerImage?.bufferFullUrl}
                      width={89}
                      height={50}
                    />
                  </div>
                </DndItem>
              ))
            ) : (
              <div className="flex min-h-[300px] flex-col items-center justify-center gap-2">
                <LocalIcon iconSrc={TablePlaceholderSvg} iconSize={56} />
                <Typo typoColor="gray2" typoType="b14r">
                  현재 노출중인 배너가 없습니다.
                </Typo>
              </div>
            )}
          </div>
        </div>
      </DndProvider>
    </Dialog>
  );
}
